import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useFragment } from "react-relay";

import {
  ScheduleOption,
  ScheduleSwitcher,
  facilityToScheduleOption,
} from "@/components/ScheduleSwitcher";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { ScreenFooter } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenFooter";
import { CourseNote } from "@whoosh/design-system/src/apps/PatronApp/screens/Agenda/CourseNote";
import { Button } from "@whoosh/design-system/src/components/controls";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  HStack,
  VStack,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import { AgendaWaitlist } from "../../../components/AgendaWaitlist";
import { agendaMemberNotes_atom } from "../../../features/MemberNotes/atoms";
import { ClubStackNavigatorParamList } from "../../../navigators/ClubNavigator";
import { selectedCoursesByFacility } from "../../../screens/AgendaScreen/AgendaScreens/MultiSlotBookingScreen/atoms/selectedCoursesByFacility";
import { MultiSlotFiltersWrapper } from "../../../screens/AgendaScreen/AgendaScreens/MultiSlotBookingScreen/components/MultiSlotCourseFilters/components/MultiSlotFiltersWrapper";
import { useFlags } from "../../../utils/launchDarkly";
import { FiltersModalFragment_facility$key } from "./__generated__/FiltersModalFragment_facility.graphql";
import { FiltersModal_session$key } from "./__generated__/FiltersModal_session.graphql";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onJoinWaitlist: (queueSlotId: string, requestedTime: string | null) => void;
  facilityRef: FiltersModalFragment_facility$key | null;
  sessionRef: FiltersModal_session$key | null;
};

export const FiltersModal = ({
  facilityRef,
  sessionRef,
  isOpen,
  onClose,
  onJoinWaitlist,
}: Props) => {
  const session = useFragment(FiltersModalSessionFragmentString, sessionRef);
  const facility = useFragment(FiltersModalFragmentString, facilityRef);
  const { memAppWaitlist: memAppWaitlistFeatureFlag } = useFlags();

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();

  const date = route?.params?.date;

  const curDate = new Date();
  const todayString = format(curDate, "yyyy-MM-dd");

  const memberNote = useAtomValue(agendaMemberNotes_atom);

  const [checkedCourses, setCheckedCourses] = useAtom(
    selectedCoursesByFacility(facility?.id ?? "")
  );

  const [currentCheckedCourses, setCurrentCheckedCourses] = useState<Record<
    string,
    boolean
  > | null>(checkedCourses);

  useEffect(() => {
    if (isOpen) {
      setCurrentCheckedCourses(checkedCourses);
    }
  }, [checkedCourses, isOpen]);

  useEffect(() => {
    if (date === "today") {
      navigation.setParams({ date: todayString });
    }
  }, [date, navigation, todayString]);

  const onReset = () => {
    setCurrentCheckedCourses(null);
    setCheckedCourses(null);
    onClose();
  };

  const onSave = () => {
    setCheckedCourses(currentCheckedCourses);
    onClose();
  };

  const navigateToSchedule = useScheduleNavigator();

  const onScheduleSwitcherChange = (option: ScheduleOption) => {
    const navigateDate = date ? date : todayString;
    navigateToSchedule(option.type, option.slug, navigateDate);
  };

  const selectedScheduleOption = facilityToScheduleOption(facility ?? null);

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      upperTitle={t`Filters`}
      hideFooterPadding
      content={
        <VStack gap={6} px={4}>
          {memberNote ? (
            <CourseNote
              header={memberNote.header}
              body={memberNote.body}
              priority={memberNote.status}
            />
          ) : null}
          <VStack gap={6} px={2}>
            <ScheduleSwitcher
              selectedOption={selectedScheduleOption}
              sessionRef={session ?? null}
              onChange={onScheduleSwitcherChange}
            />
            <MultiSlotFiltersWrapper
              facilityId={facility?.id ?? ""}
              isModalView={true}
              currentCheckedCourses={currentCheckedCourses}
              setCurrentCheckedCourses={setCurrentCheckedCourses}
            />
          </VStack>
          {memAppWaitlistFeatureFlag ? (
            <AgendaWaitlist
              date={date || ""}
              facilityId={facility?.id ?? ""}
              courseIds={[]}
              onJoinWaitlist={onJoinWaitlist}
            />
          ) : null}
        </VStack>
      }
      footerContent={
        <ScreenFooter>
          <HStack flex={1} justifyContent="space-between">
            <Box alignSelf="flex-start">
              <Button
                onPress={onReset}
                variant="ghost"
                accessibilityLabel={t`Reset filters`}
                testID="reset-filters-button"
              >{t`Reset`}</Button>
            </Box>
            <Box alignSelf="flex-end">
              <Button
                onPress={onSave}
                accessibilityLabel={t`Save and Close filters`}
                testID="save-and-close-filters-button"
              >{t`Save and Close`}</Button>
            </Box>
          </HStack>
        </ScreenFooter>
      }
    />
  );
};

export const FiltersModalFragmentString = graphql`
  fragment FiltersModalFragment_facility on Facility {
    id
    slug
    name
    type
    ...AgendaDateSelectorControlFragment_facility
    ...facilityToScheduleOption_facility
  }
`;

const FiltersModalSessionFragmentString = graphql`
  fragment FiltersModal_session on Session {
    ...ScheduleSwitcher_session
  }
`;
